import { SearchXIcon, WebhookIcon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { AppSkeletonTable } from "@/components/commons/app-skeleton-table"
import { Table, TableBody } from "@/components/ui/table"
import { useWebhooks } from "@/hooks/webhooks"

import { ColumnToggle } from "./column-toggle"
import {
  TableContent,
  TableHeaderContent,
} from "./table-components"
import {
  createWebhooksTableData,
  WEBHOOKS_DEFAULT_VISIBILITY,
} from "./table-data"

export const WebhooksTable = () => {
  const { t } = useTranslation()
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(WEBHOOKS_DEFAULT_VISIBILITY)
  const { data, isLoading, error } = useWebhooks({})

  const toggleColumnVisibility = (key: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const resetToDefault = () => {
    setColumnVisibility(WEBHOOKS_DEFAULT_VISIBILITY)
  }

  const isDefaultColumns =
    JSON.stringify(columnVisibility) ===
    JSON.stringify(WEBHOOKS_DEFAULT_VISIBILITY)

  const tableData = createWebhooksTableData({
    columnVisibility,
    t,
  })
  const configurations = data?.configurations || []
  const hasWebhooks = configurations.length > 0

  if (isLoading) return <AppSkeletonTable columns={6} />

  if (error) {
    return (
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
        <SearchXIcon size={48} strokeWidth={1.5} />
        <p className="text-center">
          {t("common.errorLoadingData")}
        </p>
      </div>
    )
  }

  if (!hasWebhooks) {
    return (
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
        <WebhookIcon size={48} strokeWidth={1.5} />
        <div className="text-center">
          <p className="font-medium">
            {t("webhooks.noWebhooksFound")}
          </p>
          <p className="text-sm">
            {t("webhooks.createFirstWebhook")}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <section className="flex items-center justify-end gap-2">
        <ColumnToggle
          columns={tableData}
          columnVisibility={columnVisibility}
          toggleColumnVisibility={toggleColumnVisibility}
          resetToDefault={resetToDefault}
          isDefaultColumns={isDefaultColumns}
        />
      </section>
      <div className="relative overflow-y-auto overflow-x-hidden rounded-md border border-border">
        <Table
          className="relative h-10 w-full overflow-clip rounded-md border-border"
          divClassname="max-h-[68vh] overflow-y-scroll scrollbar"
        >
          <TableHeaderContent tableData={tableData} />
          <TableBody>
            <TableContent
              data={configurations}
              tableData={tableData}
            />
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
